import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ColorLensOutlinedIcon from '@mui/icons-material/ColorLensOutlined';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useCreate, useDelete, useNotify, useRecordContext, useUpdate } from 'react-admin';
import { useArtFiles } from './ArtFilesContext';
import { extractPalette } from '../lib/extractPalette';
import { getExtension, inspectFile, resolveMimeType } from '../lib/inspectFile';
import { AppArt, AppArtBucket, AppArtFile, plateRecipeOf } from '../types';
import { adjustableModels, artboardsFor, isArtboard } from '../lib/artboards';
import AdjustDialog from './AdjustDialog';
import AssetCard from './AssetCard';
import DownloadAllButton from './DownloadAllButton';
import Lightbox from './Lightbox';
import MockupDialog from './MockupDialog';
import StationPlateDialog from './StationPlateDialog';

const FILES_RESOURCE = 'app-art-files';

interface Props {
  bucket: AppArtBucket;
  hint: string;
  accept: Record<string, string[]>;
  readOnly?: boolean;
  /** Habilita a ação de eleger (logo principal / modelo escolhido). */
  showPrimary?: boolean;
  /** Rótulo da ação, por bucket. */
  primaryLabel?: string;
  primaryActiveLabel?: string;
  /** Cards maiores, para a folha de mockup do modelo (duas telas). */
  tall?: boolean;
  /** Botão de extrair paleta, só nas fotos do posto. */
  onPaletteExtracted?: (colors: string[]) => void;
  /** Abre o mockup ao vivo a partir do card — só faz sentido nos modelos. */
  showMockup?: boolean;
  /** Monta o comando de ajuste a partir do card — idem. */
  showAdjust?: boolean;
  /** Compõe o QR e o código sobre o gabarito — só faz sentido nas placas. */
  showPlate?: boolean;
}

const AssetGallery = ({
  bucket, hint, accept, readOnly, showPrimary, primaryLabel, primaryActiveLabel,
  tall, onPaletteExtracted, showMockup, showAdjust, showPlate,
}: Props) => {
  const record = useRecordContext<AppArt>();
  const notify = useNotify();
  const { report } = useArtFiles();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [remove] = useDelete();

  /**
   * A lista fica local em vez de sair de `useRefresh()`: o refresh refaz o
   * getOne e o `<Form>` reseta os campos a partir do novo record, o que apagaria
   * o briefing digitado e ainda não salvo.
   */
  const initial = useMemo(
    () => (record?.files || []).filter(file => file.bucket === bucket),
    [bucket, record?.files]);

  const [files, setFiles] = useState<AppArtFile[]>(initial);
  const [uploading, setUploading] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [mockupOf, setMockupOf] = useState<AppArtFile | null>(null);
  const [adjustOf, setAdjustOf] = useState<AppArtFile | null>(null);
  const [plateOf, setPlateOf] = useState<AppArtFile | null>(null);

  useEffect(() => { setFiles(initial); }, [initial]);

  /**
   * Artboard cru não é card: ele é o fundo que o mockup ao vivo usa. Sem este
   * filtro, um modelo apareceria três vezes na galeria e a contagem diria o
   * triplo do que a rede tem.
   */
  const cards = useMemo(() => files.filter(file => !isArtboard(file)), [files]);

  /* Sobre `files`, e não sobre `record`: quem acabou de subir uma rodada
     precisa ver o modelo novo no seletor sem recarregar a tela. */
  const ajustaveis = useMemo(() => adjustableModels(files), [files]);

  /** Os dois artboards do modelo que o mockup está mostrando. */
  const mockupArt = useMemo(() => artboardsFor(files, mockupOf), [files, mockupOf]);

  // A toolbar precisa do dado vivo: `record.files` para de valer assim que
  // alguém sobe ou marca um arquivo aqui.
  const hasPrimary = cards.some(file => file.isPrimary);
  useEffect(() => {
    report(bucket, { count: cards.length, hasPrimary });
  }, [bucket, cards.length, hasPrimary, report]);

  const chainId = record?.chainId;

  const uploadOne = useCallback(async (file: File) => {
    if (!chainId) { return; }
    const inspected = await inspectFile(file, bucket);
    try {
      const created = await create(FILES_RESOURCE, {
        data: {
          chainId,
          imagefile: file,
          previewfile: inspected.previewBlob
            ? new File([inspected.previewBlob], 'preview.png', { type: 'image/png' })
            : null,
          bucket,
          fileName: file.name,
          fileExtension: getExtension(file.name),
          // Mime explícito: o sniff do backend classifica SVG como
          // application/xml e o browser passa a mostrar a árvore de tags.
          mimeType: resolveMimeType(file),
          byteSize: file.size,
          width: inspected.width,
          height: inspected.height,
          hasAlpha: inspected.hasAlpha,
          pageCount: inspected.pageCount,
          warnings: inspected.warnings,
        },
      }, { returnPromise: true }) as AppArtFile;

      setFiles(current => [...current, created]);
      if (inspected.warnings.length) {
        notify(`${file.name}: ${inspected.warnings[0]}`, { type: 'warning' });
      }
    } catch (error: any) {
      notify(`Falha ao enviar ${file.name}: ${error?.message || 'erro desconhecido'}`, { type: 'error' });
    } finally {
      if (inspected.objectUrl) { URL.revokeObjectURL(inspected.objectUrl); }
    }
  }, [bucket, chainId, create, notify]);

  const upload = useCallback(async (accepted: File[]) => {
    if (!accepted.length) { return; }
    setUploading(count => count + accepted.length);
    // Sequencial: cada arquivo é uma request, e subir 12 fotos de uma vez em
    // paralelo estoura o timeout do upload no backend.
    for (const file of accepted) {
      await uploadOne(file);
      setUploading(count => count - 1);
    }
  }, [uploadOne]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept,
    disabled: readOnly,
    noClick: true,
    onDrop: upload,
  });

  // Colar direto do WhatsApp Web é literalmente o gesto de quem está no
  // atendimento — Ctrl+V com a imagem na área de transferência.
  useEffect(() => {
    if (readOnly) { return; }
    const onPaste = (event: ClipboardEvent) => {
      const pasted = [...(event.clipboardData?.files || [])];
      if (pasted.length) { upload(pasted); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [readOnly, upload]);

  const setPrimary = async (file: AppArtFile) => {
    setBusyId(file.id);
    try {
      await update(FILES_RESOURCE, {
        id: file.id,
        data: { isPrimary: true },
        previousData: file,
      }, { returnPromise: true });
      setFiles(current => current.map(item => ({ ...item, isPrimary: item.id === file.id })));
    } catch (error: any) {
      notify(error?.message || 'Não foi possível marcar como principal', { type: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const removeFile = async (file: AppArtFile) => {
    setBusyId(file.id);
    try {
      await remove(FILES_RESOURCE, { id: file.id, previousData: file }, { returnPromise: true });
      setFiles(current => {
        const rest = current.filter(item => item.id !== file.id);
        // Bucket não pode ficar sem principal — o backend promove a próxima.
        if (file.isPrimary && rest.length) {
          return rest.map((item, index) => ({ ...item, isPrimary: index === 0 }));
        }
        return rest;
      });
    } catch (error: any) {
      notify(error?.message || 'Não foi possível remover', { type: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const runPaletteExtraction = async () => {
    const source = files.find(f => f.previewUrl || f.mimeType?.startsWith('image/'));
    if (!source) { return; }
    setExtracting(true);
    try {
      const colors = await extractPalette(source.previewUrl || source.fileUrl);
      if (!colors.length) {
        notify('Não foi possível ler as cores dessa imagem.', { type: 'warning' });
        return;
      }
      onPaletteExtracted?.(colors);
    } finally {
      setExtracting(false);
    }
  };

  if (!record) { return null; }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="caption" color="text.secondary">{hint}</Typography>
        {!!uploading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', flexShrink: 0 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              enviando {uploading}…
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        {...getRootProps()}
        sx={{
          p: 2,
          border: '1px dashed',
          borderRadius: 1,
          borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: isDragActive ? 'action.hover' : 'transparent',
        }}
      >
        <input {...getInputProps()} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {cards.map(file => (
            <AssetCard
              key={file.id}
              file={file}
              onOpenMockup={showMockup ? () => setMockupOf(file) : undefined}
              onAdjust={showAdjust && file.modelKey ? () => setAdjustOf(file) : undefined}
              onOpenPlate={showPlate ? () => setPlateOf(file) : undefined}
              showPrimary={showPrimary}
              primaryLabel={primaryLabel}
              primaryActiveLabel={primaryActiveLabel}
              tall={tall}
              readOnly={readOnly}
              busy={busyId === file.id}
              onOpen={() => setOpenIndex(cards.findIndex(item => item.id === file.id))}
              onSetPrimary={setPrimary}
              onRemove={removeFile}
            />
          ))}

          {!readOnly && (
            <Box
              onClick={open}
              sx={{
                width: tall ? 360 : 168, minHeight: tall ? 430 : 180, cursor: 'pointer',
                border: '1px dashed', borderColor: 'divider', borderRadius: 1,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', color: 'text.secondary',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
              }}
            >
              <CloudUploadOutlinedIcon />
              <Typography variant="caption" sx={{ mt: 0.5 }}>Adicionar</Typography>
              <Typography variant="caption" sx={{ fontSize: 10 }}>arraste, clique ou cole</Typography>
            </Box>
          )}
        </Box>

        {!cards.length && readOnly && (
          <Typography variant="body2" color="text.secondary">Nenhum arquivo enviado.</Typography>
        )}
      </Box>

      <Lightbox
        files={cards}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onNavigate={setOpenIndex}
      />

      {showMockup && (
        <MockupDialog
          open={!!mockupOf}
          onClose={() => setMockupOf(null)}
          record={record}
          title={mockupOf?.caption || mockupOf?.fileName || 'Ver no app'}
          model={mockupOf}
          welcomeArt={mockupArt.welcome}
          headerArt={mockupArt.header}
          editable={!readOnly}
        />
      )}

      {showPlate && (
        <StationPlateDialog
          open={!!plateOf}
          onClose={() => setPlateOf(null)}
          file={plateOf}
          recipe={plateRecipeOf(plateOf)}
          chainId={record.chainId}
          chainName={record.chainName}
        />
      )}

      {/* `key` no modelo: reabrir o diálogo por outro card precisa começar
          naquele modelo e com o campo limpo, não no que sobrou do anterior. */}
      {showAdjust && (
        <AdjustDialog
          key={adjustOf?.id || 'nenhum'}
          open={!!adjustOf}
          onClose={() => setAdjustOf(null)}
          chainId={record.chainId}
          chainName={record.chainName}
          models={ajustaveis}
          initial={adjustOf}
        />
      )}

      {!!cards.length && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        {onPaletteExtracted && (
          <Button
            size="small"
            startIcon={extracting ? <CircularProgress size={14} /> : <ColorLensOutlinedIcon />}
            onClick={runPaletteExtraction}
            disabled={extracting || readOnly}
          >
            Extrair paleta das fotos
          </Button>
        )}

        {/* `files` e não `record.files`: quem acabou de subir uma foto espera
            que ela venha no "baixar todos" sem recarregar a tela. */}
        <DownloadAllButton files={files} />
      </Box>
      )}
    </Box>
  );
};

export default AssetGallery;
